
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HelpCircle, Link2, ArrowRight } from 'lucide-react';
import { validateAndNormalizeUrl, debugUrlTransformation } from '@/utils/urlUtils';

interface UrlInputProps {
  onSubmit: (url: string) => void;
  isLoading: boolean;
}

const UrlInput: React.FC<UrlInputProps> = ({ onSubmit, isLoading }) => {
  const [url, setUrl] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url) {
      toast.error('Please enter a URL');
      return;
    }
    
    // Debug URL transformation
    debugUrlTransformation(url, 'UrlInput.handleSubmit');
    
    // Use comprehensive URL validation
    const validation = validateAndNormalizeUrl(url);
    
    if (!validation.isValid) {
      toast.error(validation.error || 'Please enter a valid URL');
      console.error(`[UrlInput] URL validation failed:`, validation);
      return;
    }
    
    console.log(`[UrlInput] Submitting normalized URL: "${validation.normalizedUrl}"`);
    onSubmit(validation.normalizedUrl);
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-2xl mx-auto">
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground z-10">
            <Link2 size={18} />
          </div>
          <Input
            type="text"
            placeholder="Enter website URL (e.g., example.com)"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="w-full pl-12 pr-4 py-6 h-auto rounded-xl bg-secondary/95 border-white/10 text-foreground text-sm md:text-base"
            disabled={isLoading}
          />
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground cursor-help transition-colors">
                  <HelpCircle size={16} />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-sm">Enter a website URL to analyze its SEO performance.<br />
                Examples: example.com, https://example.com</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <button
          type="submit"
          disabled={isLoading}
          className={`py-4 px-8 rounded-xl font-medium text-sm md:text-base transition-all duration-300 flex items-center justify-center ${
            isLoading
              ? 'bg-secondary/70 text-muted-foreground cursor-not-allowed'
              : 'bg-primary hover:bg-primary/90 text-white'
          }`}
        >
          {isLoading ? 'Analyzing...' : (
            <>
              Analyze
              <ArrowRight className="ml-2 w-4 h-4" />
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default UrlInput;
